import React from 'react';
import ExpandableSection from '@amzn/awsui-components-core/expandable-section';
import type { ReportStudioState } from 'src/components/star-grid/hooks/useReportStudio';
import { COMMIT_ON_BLUR, useTypedText } from 'src/components/star-grid/hooks/useTypedText';
import styles from 'src/components/star-grid/components/styles/StudioHeader.module.scss';

/**
 * The report name and description, as their OWN component.
 *
 * Deliberately not inlined into {@link StudioReportDetails}: {@link useTypedText} re-renders whoever
 * owns it on each keystroke, so owning it here keeps a letter from re-rendering the deck settings,
 * the template picker and the layout select alongside it.
 */
export const StudioDetailsFields: React.FC<{ state: ReportStudioState }> = ({ state }) => {
  const name = useTypedText(state.draft.name, (value) => state.details({ name: value }), COMMIT_ON_BLUR, true);
  const description = useTypedText(state.draft.description, (value) => state.details({ description: value }), COMMIT_ON_BLUR);
  return (
    <div className={styles.nameField}>
      <label className={styles.field}>
        <span className={styles.fieldLabel}>
          {state.text('createReportName')}{' '}
          <span className={styles.required} aria-hidden="true">
            *
          </span>
        </span>
        <input
          className={styles.reportName}
          aria-label={state.text('createReportName')}
          placeholder={state.text('createNamePlaceholder')}
          required
          value={name.value}
          onChange={(event) => name.onChange(event.target.value)}
          onBlur={name.onBlur}
        />
      </label>
      <ExpandableSection
        className={styles.description}
        variant="inline"
        headerText={<span className={styles.descriptionLabel}>{state.text('studioOptionalDescription')}</span>}
        defaultExpanded={Boolean(state.draft.description)}
        data-testid="studio-description"
      >
        <textarea
          className={styles.reportDescription}
          aria-label={state.text('createDescription')}
          placeholder={state.text('createDescriptionHelp')}
          value={description.value}
          rows={2}
          onChange={(event) => description.onChange(event.target.value)}
          onBlur={description.onBlur}
        />
      </ExpandableSection>
    </div>
  );
};
